import { useSelector, useDispatch } from 'react-redux';
import {
  setColorScaleOption,
  updateCalendarStyleOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import './ColorSteps.css';
import ChartColors from './ChartColors';
import { useColorScale } from './ColorScaleComponents/useColorScale';
import { ColorInput } from './ColorScaleComponents/ColorInput';
import { MinMaxInput } from './ColorScaleComponents/MinMaxInput';
import { ScaleTypeRadio } from './ColorScaleComponents/ScaleTypeRadio';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';

const ColorScale = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, chartProperties, tabTileProps } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    tabTileProps: state.tabTileProps,
  }));

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const { severity, openAlert, testMessage, checkMinMaxValue, setOpenAlert, setTestMessage } =
    useColorScale();

  const colorScale = chartControls.properties[propKey].colorScale;
  const selectedOption = colorScale.colorScaleType;
  const calendarStyleOptions = chartControls.properties[propKey].calendarStyleOptions;
  const isManualMode = colorScale.manualColor;

  const handleColorChange = (option: string, value: string | boolean) => {
    dispatch(setColorScaleOption(option, value, propKey));
  };

  const handleCalendarColorChange = (option: string, value: string) => {
    dispatch(updateCalendarStyleOptions(propKey, option, value));
  };

  return (
    <div
      style={{
        // transform: 'Translate(2px)',
        marginTop: '20px',
        paddingTop: '8px',
        paddingLeft: '3px',
        paddingRight: '2px',
      }}
    >
      <OrientationRadioGroup
        orientation={isManualMode ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          dispatch(setColorScaleOption('manualColor', key === 'manual', propKey));
        }}
        style={{ width: '94%' }}
      />

      <div className="optionsInfo" style={{ transform: 'Translate(-2px)' }}>
        {isManualMode ? (
          <>
            <div
              className="optionDescription"
              style={{
                paddingTop: '0',
                marginTop: '5px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Set Color Scale:
            </div>

            {/* MIN COLOR */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '10px',
                paddingLeft: '5px',
              }}
            >
              <ColorInput
                color={colorScale.minColor}
                onColorChange={(color) => handleColorChange('minColor', color)}
                marginLeft="0"
              />
              <label
                className="enableDisableLabel"
                style={{
                  marginLeft: '10px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Min Color
              </label>
            </div>

            {/* MAX COLOR */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '10px',
                paddingLeft: '5px',
              }}
            >
              <ColorInput
                color={colorScale.maxColor}
                onColorChange={(color) => handleColorChange('maxColor', color)}
                marginLeft="0"
              />
              <label
                className="enableDisableLabel"
                style={{
                  marginLeft: '10px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Max Color
              </label>
            </div>

            {chartProperties.properties[propKey].chartType !== 'heatmap' && (
              <>
                <div
                  className="optionDescription"
                  style={{
                    paddingBottom: '0',
                    paddingTop: '10px',
                    marginLeft: '0',
                    color: iconColor ? iconColor : '#404040',
                  }}
                >
                  Set Min Max Values:
                </div>

                <div className="colorScaleContainer" style={{ marginBottom: '-10px' }}>
                  <ScaleTypeRadio
                    iconColor={iconColor}
                    selectedOption={selectedOption}
                    onOptionChange={(value) => handleColorChange('colorScaleType', value)}
                  />

                  {selectedOption === 'Manual' && (
                    <div style={{ transform: 'TranslateY(-10px)' }}>
                      {/* Min Value Input */}
                      <div className="inputFieldContainer">
                        <div
                          className="optionDescription"
                          style={{
                            marginLeft: '-27.5px',
                            marginTop: '-30px',
                            whiteSpace: 'nowrap',
                            color: iconColor ? iconColor : '#404040',
                          }}
                        >
                          Min Value
                        </div>
                        <div
                          style={{
                            marginTop: '20px',
                            transform: 'TranslateX(-76px)',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{
                              width: '16px',
                              height: '16px',
                              marginRight: '8px',
                            }}
                            checked={colorScale.enableMin}
                            onChange={() => handleColorChange('enableMin', !colorScale.enableMin)}
                          />
                          <MinMaxInput
                            value={colorScale.enableMin ? colorScale.min : ''}
                            onChange={(value) => handleColorChange('min', value)}
                            onBlur={() => checkMinMaxValue(colorScale.min, colorScale.max)}
                            disabled={!colorScale.enableMin}
                          />
                        </div>
                      </div>

                      {/* Max Value Input */}
                      <div className="inputFieldContainer">
                        <div
                          className="optionDescription"
                          style={{
                            marginLeft: '-27.5px',
                            marginTop: '-30px',
                            whiteSpace: 'nowrap',
                            color: iconColor ? iconColor : '#404040',
                          }}
                        >
                          Max Value
                        </div>
                        <div
                          style={{
                            marginTop: '20px',
                            transform: 'TranslateX(-77px)',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{
                              width: '16px',
                              height: '16px',
                              marginRight: '8px',
                            }}
                            checked={colorScale.enableMax}
                            onChange={() => handleColorChange('enableMax', !colorScale.enableMax)}
                          />
                          <MinMaxInput
                            value={colorScale.enableMax ? colorScale.max : ''}
                            onChange={(value) => handleColorChange('max', value)}
                            onBlur={() => checkMinMaxValue(colorScale.min, colorScale.max)}
                            disabled={!colorScale.enableMax}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {chartProperties.properties[propKey].chartType === 'calendar' &&
              (calendarStyleOptions.showDayLabel ||
                calendarStyleOptions.showMonthLabel ||
                calendarStyleOptions.showYearLabel) && (
                <>
                  <div
                    className="optionDescription"
                    style={{
                      paddingTop: '0',
                      marginTop: '10px',
                      color: iconColor ? iconColor : '#404040',
                    }}
                  >
                    Label Color:
                  </div>
                  {calendarStyleOptions.showYearLabel && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '10px',
                        paddingLeft: '10px',
                      }}
                    >
                      <ColorInput
                        color={calendarStyleOptions.yearLabelColor}
                        onColorChange={(color) =>
                          handleCalendarColorChange('yearLabelColor', color)
                        }
                        width="25%"
                        marginLeft="0"
                      />
                      <label
                        className="enableDisableLabel"
                        style={{
                          marginLeft: '10px',
                          color: iconColor ? iconColor : '#404040',
                        }}
                      >
                        Year
                      </label>
                    </div>
                  )}
                  {calendarStyleOptions.showMonthLabel && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '10px',
                        paddingLeft: '10px',
                      }}
                    >
                      <ColorInput
                        color={calendarStyleOptions.monthLabelColor}
                        onColorChange={(color) =>
                          handleCalendarColorChange('monthLabelColor', color)
                        }
                        width="25%"
                        marginLeft="0"
                      />
                      <label
                        className="enableDisableLabel"
                        style={{
                          marginLeft: '10px',
                          color: iconColor ? iconColor : '#404040',
                        }}
                      >
                        Month
                      </label>
                    </div>
                  )}
                  {calendarStyleOptions.showDayLabel && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '10px',
                        paddingLeft: '10px',
                      }}
                    >
                      <ColorInput
                        color={calendarStyleOptions.dayLabelColor}
                        onColorChange={(color) => handleCalendarColorChange('dayLabelColor', color)}
                        width="25%"
                        marginLeft="0"
                      />
                      <label
                        className="enableDisableLabel"
                        style={{
                          marginLeft: '10px',
                          color: iconColor ? iconColor : '#404040',
                        }}
                      >
                        Day
                      </label>
                    </div>
                  )}
                </>
              )}

            <NotificationDialog
              onCloseAlert={() => {
                setOpenAlert(false);
                setTestMessage('');
              }}
              severity={severity}
              testMessage={testMessage}
              openAlert={openAlert}
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorScale;
